import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CalendarBlank } from "@phosphor-icons/react";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs } from "@/lib/format";
import { formatLocalDate } from "@/components/admin/reports/report-shared";

export interface DateRangeValue {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

type PresetKey = "today" | "yesterday" | "7d" | "30d" | "90d" | "year";

interface Preset {
  key: PresetKey;
  label: string;
  getRange: (now?: Date) => DateRangeValue;
}

// react-day-picker hands out local-midnight Dates and the callers' defaults are
// built with formatLocalDate, so every value here uses the local calendar date.
// `toISOString()` would return the UTC date, which in Sarajevo (UTC+1/+2) is the
// previous day for local midnight, and the range would slide back on each click.
const fmt = formatLocalDate;

function daysAgo(n: number, now: Date = new Date()): DateRangeValue {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (n - 1));
  return { from: fmt(from), to: fmt(now) };
}

export function getPresets(t: (key: string) => string): Preset[] {
  return [
    {
      key: "today",
      label: t("datePicker.today"),
      getRange: (now = new Date()) => ({ from: fmt(now), to: fmt(now) }),
    },
    {
      key: "yesterday",
      label: t("datePicker.yesterday"),
      getRange: (now = new Date()) => {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        return { from: fmt(d), to: fmt(d) };
      },
    },
    { key: "7d", label: t("datePicker.7days"), getRange: (now) => daysAgo(7, now) },
    { key: "30d", label: t("datePicker.30days"), getRange: (now) => daysAgo(30, now) },
    { key: "90d", label: t("datePicker.90days"), getRange: (now) => daysAgo(90, now) },
    {
      key: "year",
      label: t("datePicker.thisYear"),
      getRange: (now = new Date()) => ({
        from: `${now.getFullYear()}-01-01`,
        to: fmt(now),
      }),
    },
  ];
}

/** Calendar selection (local-midnight Dates) to YYYY-MM-DD values. */
export function rangeToValue(range: DateRange | undefined): DateRangeValue | null {
  if (!range?.from) return null;
  const from = fmt(range.from);
  return { from, to: range.to ? fmt(range.to) : from };
}

function formatDisplay(from: string, to: string): string {
  const f = new Date(from + "T00:00:00");
  const t = new Date(to + "T00:00:00");
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  const optsYear: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  if (from === to) {
    return formatDateWithOptionsBs(f, optsYear);
  }
  if (f.getFullYear() === t.getFullYear()) {
    return `${formatDateWithOptionsBs(f, opts)} – ${formatDateWithOptionsBs(t, optsYear)}`;
  }
  return `${formatDateWithOptionsBs(f, optsYear)} – ${formatDateWithOptionsBs(t, optsYear)}`;
}

export function detectPreset(
  from: string,
  to: string,
  presets: Preset[],
  now: Date = new Date(),
): PresetKey | null {
  for (const p of presets) {
    const r = p.getRange(now);
    if (r.from === from && r.to === to) return p.key;
  }
  return null;
}

export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
}) {
  const { t } = useTranslation();
  const PRESETS = useMemo(() => getPresets(t), [t]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>(value);
  const activePreset = useMemo(() => detectPreset(value.from, value.to, PRESETS), [value.from, value.to, PRESETS]);
  const activeLabel = activePreset ? PRESETS.find((p) => p.key === activePreset)?.label : null;

  const calendarRange: DateRange = useMemo(() => ({
    from: new Date(draft.from + "T00:00:00"),
    to: new Date(draft.to + "T00:00:00"),
  }), [draft.from, draft.to]);

  const isDirty = draft.from !== value.from || draft.to !== value.to;

  function handleOpenChange(next: boolean) {
    if (next) setDraft(value);
    setOpen(next);
  }

  function applyDraft() {
    onChange(draft);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 bg-card text-left font-normal hover:bg-card hover:text-foreground hover:border-primary/30 sm:w-auto"
        >
          <CalendarBlank weight="duotone" className="text-primary" data-icon="inline-start" />
          {activeLabel ? (
            <>
              <span className="font-medium">{activeLabel}</span>
              <span className="text-muted-foreground">{formatDisplay(value.from, value.to)}</span>
            </>
          ) : (
            formatDisplay(value.from, value.to)
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex max-sm:flex-col">
          <div className="flex flex-col gap-0.5 border-border/60 p-2 max-sm:border-b sm:border-r">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                variant="ghost"
                size="sm"
                className={cn(
                  "justify-start font-normal hover:bg-muted/60 hover:text-foreground",
                  activePreset === p.key
                    ? "bg-primary/10 font-medium text-primary hover:bg-primary/15 hover:text-primary"
                    : "text-muted-foreground",
                )}
                onClick={() => {
                  onChange(p.getRange());
                  setOpen(false);
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-col">
            <Calendar
              mode="range"
              defaultMonth={calendarRange.from}
              selected={calendarRange}
              onSelect={(range) => {
                const next = rangeToValue(range);
                if (next) setDraft(next);
              }}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
            <div className="flex items-center justify-between gap-3 border-t border-border/60 px-3 py-2.5">
              <span className="text-sm text-muted-foreground tabular-nums">
                {formatDisplay(draft.from, draft.to)}
              </span>
              <Button size="sm" onClick={applyDraft} disabled={!isDirty}>
                {t("datePicker.apply")}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
